<template>
    <b-field v-if="dirtySelectedPeriod">
        <template slot="label">
            <div
                v-if="!showEditLabelInput"
                @dblclick="handleDoubleClickLabel"
            >
                <b-tooltip
                    v-if="this.dirtySelectedPeriod.title.length > 45"
                    :label="this.dirtySelectedPeriod.title"
                >
                    {{getLabel}}
                </b-tooltip>
                <span v-else>{{getLabel}}</span>
            </div>
            <div v-else :class="$style.editLabelContainer">
                <b-field
                    :type="emptyLabelError ? 'is-danger' : null"
                    :class="$style.labelInputContainer"
                >
                    <b-input :custom-class="$style.labelInput" :class="[emptyLabelError ? $style.labelInputDanger : null]" v-model="newLabel"></b-input>
                </b-field>
                <div>
                    <b-button
                        icon-right="check"
                        :class="$style.confirmLabelButton"
                        @click="updateLabel"
                    >
                    </b-button>
                </div>
            </div>
        </template>
        <b-datepicker
            range
            v-model="dirtyPeriodRange"
            placeholder="Click to select..."
            icon="calendar-today"
            icon-pack="mdi"
            @input="updatePeriod"
            :class="[$style.customCalendarIcon, 'calender-icon']"
            nearby-selectable-month-days
            :date-formatter="formatDateRange"
            :disabled="isDisabled"
        >
        </b-datepicker>
    </b-field>
</template>

<script lang="ts">
    import {Component, Vue, Prop, Watch} from 'vue-property-decorator';
    import { cloneDeep as _cloneDeep } from 'lodash';
    import dayjs from 'dayjs';
    import {ProjectReportPeriod} from '@/store/modules/projects/types/projects.types';
    const dirtyMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];

    @Component({ components : {} })

    export default class ProjectAnalysisPeriodPicker extends Vue {
        @Prop()
        public selectedPeriod!: ProjectReportPeriod;
        @Prop({default: false})
        public isDisabled!: boolean;
        @Prop()
        public projectAuditIds!: number[];

        public dirtySelectedPeriod: any = null;
        public dirtyPeriodRange: Date[] = [];
        public showEditLabelInput: boolean = false;
        public newLabel: string = '';
        public emptyLabelError: boolean = false;

        public mounted() {
            this.dirtySelectedPeriod = _cloneDeep(this.selectedPeriod);
            const startDate: Date = new Date(this.getStartDate);
            const endDate: Date = new Date(this.getEndDate);
            this.dirtyPeriodRange.push(startDate);
            this.dirtyPeriodRange.push(endDate);
        }

        get getStartDate(): string {
            return dayjs(this.dirtySelectedPeriod.startedAt).startOf('day').format();
        }

        get getEndDate(): string {
            if (this.dirtySelectedPeriod.endedAt !== null) {
                return dayjs(this.dirtySelectedPeriod.endedAt).endOf('day').format();
            }
            return dayjs().endOf('day').format();
        }

        get getLabel(): string {
            if (this.dirtySelectedPeriod.title.length > 45) {
                return this.dirtySelectedPeriod.title.slice(0, 43) + '...';
            }
            return this.dirtySelectedPeriod.title;
        }

        public updatePeriod(newValue: Date[]) {
            const updatedSelectedPeriod: any = this.getUpdatedSelectedPeriod(newValue);
            this.$emit('applyAnalysisPeriod', updatedSelectedPeriod);
        }

        public getUpdatedSelectedPeriod(newDateRange: Date[]): any {
            if (dayjs(this.getStartDate).diff(dayjs(newDateRange[0]).startOf('day').format()) !== 0
                ||
                dayjs(this.getEndDate).diff(dayjs(newDateRange[1]).endOf('day').format()) !== 0
            ) {
                return {
                    title: 'Custom Period',
                    startedAt: new Date(dayjs(newDateRange[0]).startOf('day').format()),
                    endedAt: new Date(dayjs(newDateRange[1]).endOf('day').format()),
                    isAudit: false,
                    auditIds: this.projectAuditIds,
                };
            } else {
                return this.dirtySelectedPeriod;
            }
        }

        public getDate(newValue: Date): string {
            return newValue.getDate() + ' ' + dirtyMonths[newValue.getMonth()] + ' ' + newValue.getFullYear();
        }

        public formatDateRange(date: Date[]): string {
            if (this.dirtySelectedPeriod.endedAt !== null) {
                return this.getDate(date[0]) + ' - ' + this.getDate(date[1]);
            }
            return this.getDate(date[0]) + ' - ' + 'Ongoing';
        }

        public handleDoubleClickLabel() {
            if (!this.dirtySelectedPeriod.isAudit) {
                this.newLabel = this.dirtySelectedPeriod.title;
                this.showEditLabelInput = true;
            }
        }

        public updateLabel() {
            if (this.newLabel !== '') {
                this.dirtySelectedPeriod.title = this.newLabel.trim();
                this.showEditLabelInput = false;
                this.$emit('applyAnalysisPeriod', this.dirtySelectedPeriod);
            }
        }

        @Watch('newLabel')
        public watchNewLabel(value: string) {
            if (value === '') {
                this.emptyLabelError = true;
            } else {
                this.emptyLabelError = false;
            }
        }
    }
</script>
<style lang="scss" module>
    .datePickerFooterContainer {
        display: flex;
        flex-direction: column;
    }
    .footerButton {
        background-color: #FAFBFC !important;
        padding-top: 11px;
        padding-bottom: 11px;
        padding-left: 18px;
        justify-content: flex-start;
        margin-top: 12px;
        border: 1px solid #dbdbdb;
        span {
            color: #444F51;
            font-size: 16px;
            font-weight: 400;
        }
    }
    .footerButton:first-child {
        margin-top: unset;
    }
    .customCalendarIcon {
        i::before {
            padding-top: 13px;
            padding-bottom: 12px;
            width: 15px;
            margin-right: 10px;
        }
    }
    .editLabelContainer {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
    }
    .labelInputContainer {
        width: 100%;
        margin-right: 10px;
        margin-bottom: unset !important;
        span {
            height: 24px !important;
            i::before {
                font-size: 16px !important;
            }
        }
    }
    .labelInput {
        font-size: 14px;
        height: 24px;
        padding-left: 8px;
        padding-top: 3px;
        padding-bottom: 3px;
        background-color: unset;
        box-shadow: unset;
        border: 1px solid #DFDFDF;
        color: #9F9F9F;
        &:focus {
            border-color: unset;
            border: 1px solid #DFDFDF;
            box-shadow: unset;
        }
    }
    .labelInputDanger {
        .labelInput {
            &:focus {
                border: 1px solid #F14668;
                box-shadow: unset;
            }
        }
    }
    .confirmLabelButton {
        height: 24px;
        width: 24px;
        background-color: transparent;
        padding: unset;
        border: unset;
        i::before {
            font-size: 24px;
            color: #21B5EA;
        }
    }
</style>
